import { useCallback, use, useEffect } from 'react';
import { useOnInView } from 'react-intersection-observer';

import { TracktorContext } from './TracktorContext';
import type { TrackEventFunction, TrackingData, UseTracktorProps } from './types';
import { computeTrackingData } from './utils/computeTrackingData';
import { wrapFunction } from './utils/wrapFunction';

export function useTracktor({
	eventData,
	intersectionOptions = { triggerOnce: true },
	pageViewData,
	trackingData: ownData = {},
}: UseTracktorProps = {}) {
	// Get the built `trackingData` and the `dispatcher` from our context.
	const { data, dispatcher } = use(TracktorContext);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (!!pageViewData && !dispatcher.isDefault) {
			// Call the `dispatcher` with the provided `pageViewData`.
			dispatcher(pageViewData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Computes the tracking data, and calls the `dispatcher` with it.
	const trackEvent: TrackEventFunction = useCallback(
		(stateOrMergeStateFn) => {
			let _stateOrMergeStateFn = stateOrMergeStateFn;

			/* this allows trackEvent to be directly used e.g. as onClick handler */
			if (
				(_stateOrMergeStateFn && 'nativeEvent' in _stateOrMergeStateFn && _stateOrMergeStateFn.nativeEvent instanceof Event) ||
				_stateOrMergeStateFn instanceof Event
			) {
				_stateOrMergeStateFn = undefined;
			}

			if (typeof _stateOrMergeStateFn === 'function') {
				const contextData = computeTrackingData(data, ownData, {});
				const mergedTrackingData = _stateOrMergeStateFn(contextData);
				dispatcher(mergedTrackingData);
			} else {
				const mergedTrackingData = computeTrackingData(data, ownData, _stateOrMergeStateFn || {});
				dispatcher(mergedTrackingData);
			}
		},
		[data, dispatcher, ownData]
	);

	// This higher-order function sets the `trackEventData` and also checks whether the library-consumer
	// defined the `eventData` which is used inside this function.
	function createTrackEvent(trackEventData: TrackingData = {}) {
		return () => {
			if (!eventData && process.env.NODE_ENV !== 'production') {
				throw new Error('If the `onClickWrapper` is used, the `eventData` prop has to be defined.');
			}

			trackEvent(trackEventData || {});
		};
	}

	// This enables the consumer to wrap a component's `onClick` prop to also fire the tracking event.
	const onClickWrapper = wrapFunction(createTrackEvent(eventData));

	const onIntersect = useCallback(
		(inView: boolean) => {
			if (inView) {
				if (!eventData && process.env.NODE_ENV !== 'production') {
					throw new Error('If the `intersectionRef` is used, the `eventData` prop has to be defined.');
				}

				trackEvent(eventData || {});
			}
		},
		[eventData, trackEvent]
	);

	const intersectionRef = useOnInView<HTMLElement>(onIntersect, intersectionOptions);

	return {
		createTrackEvent,
		intersectionRef,
		onClickWrapper,
		/**
		 * if an object is provided, we deep merge it when withoutContext is falsy.
		 * If a function is provided, merging has to be done manually by the user through this function based on given state.
		 */
		trackEvent,
	};
}
